import { z } from 'zod';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import {
  EQUIPMENT_USABILITY,
  HSE_ACTION_KINDS,
  HSE_CATEGORIES,
  HSE_DEPARTMENTS,
  HSE_EXTERNAL_AUTHORITIES,
  HSE_LEGACY_PREFIXES,
  HSE_PREFIXES,
  HSE_PRIORITIES,
  HSE_REPORT_TYPES,
  HSE_SUB_CATEGORIES,
  type HseReportType,
  NC_CLASSES,
  NC_SOURCES,
  NOTIFICATION_DECISIONS,
  NOTIFICATION_RECIPIENTS,
  PSC_DOCUMENTS,
  PSC_MOUS,
  PSC_OVERALL_FINDINGS,
} from './constants.js';

/**
 * HSE raporunun şeması (ADR 0049 §1): ortak alanlar ve türe özgü `details`. Taslak gevşek
 * doğrulanır (biçim ve liste); gönderimde zorunlu alanlar `hseMissingForSubmit` ile istenir.
 * Gemi, bulut ve arayüz aynı şemayı kullanır. Hata iletileri koddur.
 */

const collapse = (value: string) => value.normalize('NFC').trim();
/** Boş metin `null` olur; uzunluk sınırı aşılırsa `TOO_LONG`. */
const text = (max: number) =>
  z
    .string()
    .nullish()
    .transform((value) => {
      if (value === null || value === undefined) return null;
      const trimmed = collapse(value);
      return trimmed === '' ? null : trimmed;
    })
    .pipe(z.string().max(max, 'TOO_LONG').nullable());
const choice = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, { message: 'INVALID' }).nullish().default(null);
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'INVALID_DATE')
  .nullish()
  .default(null);
const instant = z.iso.datetime({ offset: false, message: 'INVALID_TIME' }).nullish().default(null);
const id = z.uuid({ message: 'INVALID_ID' }).nullish().default(null);

const pscSchema = z.object({
  authority: text(200),
  mou: choice(PSC_MOUS),
  port: text(200),
  inspectionDate: day,
  inspectionNo: text(100),
  inspectorName: text(200),
  reportNo: text(100),
  issuedDocument: choice(PSC_DOCUMENTS),
  remarkReference: text(200),
  overallFinding: choice(PSC_OVERALL_FINDINGS),
  findingNote: text(2000),
});

const yesNo = choice(['yes', 'no'] as const);
const scale = z.number().int().min(1).max(5).nullish().default(null);

/** Kullanılan malzeme (H2 Implementation): parça / kod, miktar, stoktan mı, talep no. */
const materialItem = z.object({
  item: text(200),
  code: text(100),
  quantity: z.number().min(0).max(100_000).nullish().default(null),
  unit: text(20),
  fromStock: z.boolean().nullish().default(null),
  requisitionNo: text(100),
});

/**
 * Dört türün ortak HSEQ alanları (ortak 2026-10-20b H2; ADR 0097 §4): 10 basamağın türden bağımsız
 * bölümleri `details` içinde. Risk seviyesi saklanmaz, şirket matrisiyle türer (ADR 0097 §4).
 */
const hseqShape = {
  /** 2. Initial Assessment: kaptanın ilk değerlendirmesi. */
  initialAssessment: text(4000),
  /** 3. Risk Assessment: olası sonuçlar, mevcut kontroller, olasılık, şiddet. */
  risk: z
    .object({
      consequences: text(2000),
      existingControls: text(2000),
      likelihood: scale,
      severity: scale,
    })
    .nullish()
    .default(null),
  /** 7. Implementation: yapılan işlemler ve kullanılan malzemeler (kanıt eklerde). */
  implementation: z
    .object({
      workDone: text(4000),
      materials: z.array(materialItem).max(50).default([]),
    })
    .nullish()
    .default(null),
  /** 8. Verification: faaliyet etkin mi, tarih, doğrulayan (açıklama `verificationNote`). */
  verification: z
    .object({ effective: yesNo, date: day, verifiedBy: text(200) })
    .nullish()
    .default(null),
  /** SMS prosedür güncellemesi ihtiyacı (H10; LGS-17 SMS bölümü kalıbı). */
  smsUpdate: z
    .object({
      needed: z.boolean().nullish().default(null),
      procedure: text(300),
      proposal: text(2000),
    })
    .nullish()
    .default(null),
};

const nonConformityDetails = z.object({
  ...hseqShape,
  /** H5: ihlal edilen ISM / SMS maddesi; gönderimde zorunlu. */
  smsReference: text(300),
  ncClass: choice(NC_CLASSES),
  rootCauseHypothesis: text(4000),
  proposedAction: text(4000),
  psc: pscSchema.nullish().default(null),
});

const deficiencyDetails = z.object({
  ...hseqShape,
  /** H6: eksiklik operasyonu engelliyor mu; dış referans (PSC / Flag / Class). */
  impedesOperation: yesNo,
  externalAuthority: choice(HSE_EXTERNAL_AUTHORITIES),
  externalReference: text(200),
  assetNo: text(100),
  locationDetail: text(200),
  manufacturer: text(200),
  model: text(200),
  operationalImpact: text(2000),
  equipmentUsable: choice(EQUIPMENT_USABILITY),
  temporaryMeasure: text(2000),
  spareEquipment: text(500),
  serviceNeed: text(2000),
  testResult: text(2000),
  fitForService: choice(['yes', 'no'] as const),
});

const nearMissDetails = z.object({
  ...hseqShape,
  /** H7: ramak kala koşulları ve insan faktörleri. */
  conditions: text(2000),
  humanFactors: text(2000),
  chronology: text(8000),
  worstOutcome: text(2000),
  affected: text(2000),
  riskLevel: choice(HSE_PRIORITIES),
  riskMethod: text(500),
  preventingFactors: text(2000),
  similarEvents: text(2000),
  residualRisk: choice(HSE_PRIORITIES),
  shareWithFleet: z.boolean().nullish().default(null),
});

const notificationItem = z.object({
  recipient: z.enum(NOTIFICATION_RECIPIENTS, { message: 'INVALID' }),
  name: text(200),
  /** Son gün (H8): kişi girer; süre bayrağa ve SMS'e göre değişir (ADR 0097 §5). */
  dueDate: day,
  notifiedAt: instant,
  method: text(200),
  reference: text(200),
});

const accidentDetails = z.object({
  ...hseqShape,
  injuredCount: z.number().int().min(0).max(1000).nullish().default(null),
  injuries: text(4000),
  damage: text(4000),
  environmental: text(4000),
  emergencyResponse: text(4000),
  medicalMeasures: text(4000),
  witnesses: text(8000),
  chronology: text(8000),
  contributingFactors: text(4000),
  losses: text(2000),
  notification: z
    .object({
      decision: choice(NOTIFICATION_DECISIONS),
      reason: text(2000),
      items: z.array(notificationItem).max(20).default([]),
    })
    .nullish()
    .default(null),
});

const DETAILS: Record<HseReportType, z.ZodType> = {
  non_conformity: nonConformityDetails,
  deficiency: deficiencyDetails,
  near_miss: nearMissDetails,
  accident: accidentDetails,
};

export type PscDetails = z.infer<typeof pscSchema>;
export type NonConformityDetails = z.infer<typeof nonConformityDetails>;
export type DeficiencyDetails = z.infer<typeof deficiencyDetails>;
export type NearMissDetails = z.infer<typeof nearMissDetails>;
export type AccidentDetails = z.infer<typeof accidentDetails>;
export type NotificationItem = z.infer<typeof notificationItem>;
const hseqCommon = z.object(hseqShape);
/** Dört türün ortak HSEQ alanları (ADR 0097 §4). */
export type HseqCommonDetails = z.infer<typeof hseqCommon>;
export type HseqMaterial = z.infer<typeof materialItem>;

const common = z.object({
  reportType: z.enum(HSE_REPORT_TYPES, { message: 'INVALID' }),
  title: text(200),
  description: text(8000),
  occurredAt: instant,
  tzOffsetMin: z.number().int().min(-840).max(840),
  location: text(200),
  department: choice(HSE_DEPARTMENTS),
  reportedBy: text(200),
  category: z.string().nullish().default(null),
  subCategory: choice(HSE_SUB_CATEGORIES),
  priority: choice(HSE_PRIORITIES),
  source: choice(NC_SOURCES),
  responsible: text(200),
  targetDate: day,
  immediateAction: text(4000),
  investigation: text(8000),
  rootCause: text(4000),
  verificationNote: text(4000),
  lessonsLearned: text(4000),
  details: z.unknown(),
  parentReportId: id,
  equipmentId: id,
  workOrderId: id,
  defectId: id,
  certificateId: id,
  formRecordId: id,
});

export interface HseReportInput {
  reportType: HseReportType;
  title: string | null;
  description: string | null;
  occurredAt: string | null;
  tzOffsetMin: number;
  location: string | null;
  department: string | null;
  reportedBy: string | null;
  category: string | null;
  subCategory: string | null;
  priority: string | null;
  source: string | null;
  responsible: string | null;
  targetDate: string | null;
  immediateAction: string | null;
  investigation: string | null;
  rootCause: string | null;
  verificationNote: string | null;
  lessonsLearned: string | null;
  details: Record<string, unknown>;
  parentReportId: string | null;
  equipmentId: string | null;
  workOrderId: string | null;
  defectId: string | null;
  certificateId: string | null;
  formRecordId: string | null;
}

function issuesOf(error: z.ZodError, prefix: string[] = []): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: [...prefix, ...issue.path.map(String)].join('.'),
    code: issue.code === 'invalid_type' && issue.input === undefined ? 'REQUIRED' : issue.message,
  }));
}

const present = (value: unknown) =>
  value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '');

/** Gönderimde boş olamayan ortak alanlar. */
const COMMON_REQUIRED = [
  'title',
  'description',
  'occurredAt',
  'location',
  'department',
  'reportedBy',
  'category',
  'priority',
] as const;

/**
 * Gönderimde eksik alanlar (ADR 0049 §1): ortak alanlar, türe özgü zorunlular (ortağın örnek
 * ekranlarında yıldızlı alanlar ve §4). Yol `details.psc.port` biçimindedir.
 */
export interface HseRuleOptions {
  /**
   * Eski sürüm kuralı (ADR 0097 §6): bulut eski gemi sürümünün paketini uygularken HSEQ ile gelen
   * yeni zorunluluklar (ihlal edilen madde, etkinlik doğrulaması) aranmaz; senkron durmaz.
   */
  legacy?: boolean;
}

export function hseMissingForSubmit(
  report: HseReportInput,
  options: HseRuleOptions = {},
): string[] {
  const missing: string[] = COMMON_REQUIRED.filter((key) => !present(report[key]));
  const details = (report.details ?? {}) as Record<string, unknown>;
  const need = (path: string, value: unknown) => {
    if (!present(value)) missing.push(path);
  };
  switch (report.reportType) {
    case 'non_conformity': {
      need('source', report.source);
      // H5: ihlal edilen ISM / SMS maddesi zorunlu [ISM 9.1] (ADR 0097 §5). PSC kaynaklıda madde
      // her remark'ın "Regulation Ref." alanındadır (ADR 0049 §9)
      if (report.source === 'psc_inspection') {
        const psc = (details.psc ?? {}) as Record<string, unknown>;
        for (const key of ['authority', 'mou', 'port', 'inspectionDate', 'overallFinding']) {
          need(`details.psc.${key}`, psc[key]);
        }
      } else if (!options.legacy) {
        need('details.smsReference', details.smsReference);
      }
      break;
    }
    case 'deficiency':
      need('subCategory', report.subCategory);
      need('details.equipmentUsable', details.equipmentUsable);
      break;
    case 'near_miss':
      need('immediateAction', report.immediateAction);
      need('details.worstOutcome', details.worstOutcome);
      need('details.riskLevel', details.riskLevel);
      break;
    case 'accident':
      need('immediateAction', report.immediateAction);
      break;
  }
  return missing;
}

/**
 * Raporu doğrular ve biçimler. `draft`: biçim ve liste denetimi; `submit`: ayrıca zorunlu alanlar
 * (`REQUIRED`). Kategori türün listesinden olmalı. Gelecek tarih kayıt katmanındadır.
 */
export function parseHseReport(
  input: unknown,
  stage: 'draft' | 'submit',
  options: HseRuleOptions = {},
): ParseResult<HseReportInput> {
  const head = common.safeParse(input);
  if (!head.success) return { ok: false, issues: issuesOf(head.error) };
  const value = head.data;
  const issues: SchemaIssue[] = [];
  if (value.category !== null && !HSE_CATEGORIES[value.reportType].includes(value.category)) {
    issues.push({ path: 'category', code: 'INVALID' });
  }
  if (value.source !== null && value.reportType !== 'non_conformity') {
    issues.push({ path: 'source', code: 'INVALID' });
  }
  if (value.subCategory !== null && value.reportType !== 'deficiency') {
    issues.push({ path: 'subCategory', code: 'INVALID' });
  }
  const details = DETAILS[value.reportType].safeParse(value.details ?? {});
  if (!details.success) issues.push(...issuesOf(details.error, ['details']));
  if (issues.length > 0) return { ok: false, issues };
  const report: HseReportInput = {
    ...value,
    details: details.success ? (details.data as Record<string, unknown>) : {},
  };
  if (stage === 'submit') {
    const missing = hseMissingForSubmit(report, options);
    if (missing.length > 0) {
      return { ok: false, issues: missing.map((path) => ({ path, code: 'REQUIRED' })) };
    }
  }
  return { ok: true, value: report };
}

/** Numara: önek, yıl, en az üç hane (`NC-2026-001`, `DF-2026-004`). */
export function hseReportNo(type: HseReportType, year: number, seq: number): string {
  return `${HSE_PREFIXES[type]}-${year}-${String(seq).padStart(3, '0')}`;
}

/**
 * Kayıttaki numara geçerli mi (ADR 0097 §2): bugünkü önekle ya da türün eski önekiyle (`DEF-`).
 * Eski sürümdeki geminin paketi bulutta yine kabul edilir; numara yeniden yazılmaz.
 */
export function isHseReportNo(
  type: HseReportType,
  year: number,
  seq: number,
  no: unknown,
): boolean {
  const tail = `-${year}-${String(seq).padStart(3, '0')}`;
  return [HSE_PREFIXES[type], ...(HSE_LEGACY_PREFIXES[type] ?? [])].some(
    (prefix) => no === `${prefix}${tail}`,
  );
}

/** Raporun tür kısaltması (NC / DF / NM / ACC): liste süzgeci ve rozet. */
export function hseTypeCode(type: HseReportType): string {
  return HSE_PREFIXES[type];
}

/** Sıradaki numara: gemi yerel gününün yılında, gemi ve tür başına boşluksuz. */
export function nextHseNumber(
  type: HseReportType,
  localDate: string,
  lastSeq: number,
): { year: number; seq: number; reportNo: string } {
  const year = Number(localDate.slice(0, 4));
  const seq = lastSeq + 1;
  return { year, seq, reportNo: hseReportNo(type, year, seq) };
}

/* ---------- Faaliyet ve bulgu ---------- */

const requiredText = (max: number) =>
  z
    .string({ message: 'REQUIRED' })
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));

const actionSchema = z.object({
  kind: z.enum(HSE_ACTION_KINDS, { message: 'REQUIRED' }),
  description: requiredText(2000),
  responsible: requiredText(200),
  responsibleUserId: id,
  dueDate: z.string({ message: 'REQUIRED' }).regex(/^\d{4}-\d{2}-\d{2}$/, 'INVALID_DATE'),
  findingId: id,
  workOrderId: id,
  formRecordId: id,
});
export type HseActionInput = z.infer<typeof actionSchema>;

export function parseHseAction(input: unknown): ParseResult<HseActionInput> {
  const result = actionSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issuesOf(result.error) };
}

const findingSchema = z.object({
  description: requiredText(1000),
  category: text(200),
  regulationRef: text(200),
  // PSC kodu ve alınan önlem kodu serbest metindir: kod listesi lisansı doğrulanmadı (ADR 0049 §9)
  code: text(20),
  actionTakenCode: text(20),
  dueDate: day,
  linkedReportId: id,
});
export type HseFindingInput = z.infer<typeof findingSchema>;

export function parseHseFinding(input: unknown): ParseResult<HseFindingInput> {
  const result = findingSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issuesOf(result.error) };
}
